import React, { useContext, useState } from "react";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Loader, ScrollableContainer, StyledPageWrapper, Title, ViewportContext } from "@monorepo/components";
import { Modal } from "@monorepo/components";
import { LANGUAGE } from "@monorepo/constants";
import { routeConfig } from "@monorepo/constants/routeConfig";
import { convertNumberToCurrency, showErrorArrayTitles } from "@monorepo/helpers";
import { BankIcon, EuroIcon } from "@monorepo/icons";
import { useGetCartGroupByIdQuery } from "@monorepo/services/api/carts";
import {
  orderApi,
  useGetActiveOrderQuery,
  useGetPaymentOptionsQuery,
  useProccessPaymentMutation,
} from "@monorepo/services/api/order";
import { initialAttributesState } from "@monorepo/slices/attributes";
import theme from "@monorepo/styles/theme";
import {
  IActiveCartSliceState,
  IAttributesSliceState,
  LanguagesEnum,
  PaymentOptionEnum,
  SuccessOrderCreationMessageEnum,
} from "@monorepo/types";
import LoadingButton from "@mui/lab/LoadingButton";
import { Box, IconButton, styled } from "@mui/material";
import Typography from "@mui/material/Typography";
import { reverse } from "named-urls";

import PaymentCoupons from "../PaymentCoupons/PaymentCoupons";
import PaymentSumsBox from "./PaymentSumsBox";

const PaymentType = styled(Box)(() => ({
  display: "grid",
  alignContent: "space-between",
  gridRowGap: theme.spacing(1),
  minHeight: "150px",
  textAlign: "center",
}));

const PaymentButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "active",
})<{ active: boolean }>(({ active }) => ({
  "&:hover": {
    color: active ? theme.palette.background.paper : theme.palette.primary.main,
    backgroundColor: active ? theme.palette.primary.main : theme.palette.secondary.dark,
  },
  placeSelf: "center",
  width: "90px",
  height: "90px",
  borderRadius: "50%",
  transformOrigin: "center",
  backgroundColor: active ? theme.palette.primary.main : theme.palette.geyserGray.main,
  color: active ? theme.palette.background.paper : theme.palette.text.secondary,
  transition: `transform ${theme.transitions.duration.standard / 1000}s ${theme.transitions.easing.easeInOut}`,
  transform: active ? "scale(1.2)" : "scale(1)",
  "& > svg": {
    fontSize: "3rem",
  },
}));

interface IProps {
  isPaymentBankOnly?: boolean;
}

const PaymentOverview: React.FC<IProps> = ({ isPaymentBankOnly = false }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const { data: paymentOptions } = useGetPaymentOptionsQuery();
  const [paymentOption, setPaymentOption] = useState<PaymentOptionEnum>(PaymentOptionEnum.MIP);
  const activeCartId = useSelector(({ activeCartSlice }: IActiveCartSliceState) => activeCartSlice.cartId);
  const { parentOriginUrl, externalPaymentUrl } =
    useSelector(({ attributesSlice }: IAttributesSliceState) => attributesSlice?.["attributes-object"]) ||
    initialAttributesState["attributes-object"];
  const { isThirdParty } = useContext(ViewportContext);
  const { data: activeCart } = useGetCartGroupByIdQuery(activeCartId, { skip: !activeCartId });
  const {
    data: activeOrder,
    error,
    isError,
    isLoading: isOrderLoading,
    isFetching,
  } = useGetActiveOrderQuery(undefined as void, { refetchOnMountOrArgChange: true });

  const [processPayment, { isLoading }] = useProccessPaymentMutation();

  const commission = activeOrder?.commission;
  const sum = activeOrder?.sum;
  const total = activeOrder?.total;
  const orderId = activeOrder?.id;
  const discount = activeOrder?.discount;
  const isDiscountApplied = discount && discount !== "0.00" ? true : false;
  const [showModal, setShowModal] = useState(false);
  const [isCouponUsed, setIsCouponUsed] = useState(isDiscountApplied);

  const handleShowModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleCouponUsed = () => {
    handleCloseModal();
    setIsCouponUsed(true);
  };

  const handleCouponRemoved = () => {
    handleCloseModal();
    setIsCouponUsed(discount || discount === "0.00" ? false : true);
  };

  if (isOrderLoading) {
    return <Loader />;
  }

  if (isError && Array.isArray(error)) {
    error.forEach((err) => {
      const errMsg = err?.detail || err.title;
      toast.error(errMsg);
    });
    return <Redirect to={routeConfig.dashboard.payments.self} />;
  }

  const handlePaymentProcess = async (id: string) => {
    if (isThirdParty && !!parentOriginUrl && !!externalPaymentUrl) {
      window.parent.postMessage(
        { url: externalPaymentUrl + "?orderId=" + id, eventType: "redirect", type: "third-party" },
        parentOriginUrl
      );
      return;
    }
    switch (paymentOption) {
      case PaymentOptionEnum.MIP:
        ReactGA.event("e_bank");
        break;
      case PaymentOptionEnum.CASH:
        ReactGA.event("cash");
        break;
    }
    processPayment({ id, paymentOption })
      .unwrap()
      .then((res) => {
        dispatch(orderApi.util.prefetch("getActiveOrder", undefined, { force: true }));
        if (res.message === SuccessOrderCreationMessageEnum.BANKS) {
          history.push(reverse(routeConfig.dashboard.order.mip, { orderId: id }), { banks: res.banks });
        } else if (res.message === SuccessOrderCreationMessageEnum.CASH) {
          history.push(reverse(routeConfig.dashboard.order.cash, { orderId: id }));
        }
      })
      .catch((err) => {
        showErrorArrayTitles(err);
      });
  };

  const paymentOptionsExist = typeof paymentOptions !== "undefined" && paymentOptions?.length > 0;

  return (
    <>
      <StyledPageWrapper>
        <Box sx={{ display: "grid" }}>
          <Title> {t("payments.payment_method")}</Title>
          <Typography
            textAlign="center"
            sx={{
              paddingBottom: {
                sm: theme.spacing(1),
              },
              paddingTop: {
                xs: theme.spacing(1),
              },
              color: theme.palette.gullGray.main,
            }}
          >
            {activeCart?.name}
          </Typography>
          {!externalPaymentUrl && LANGUAGE !== LanguagesEnum.PL && (
            <Typography textAlign="center" sx={{ color: theme.palette.text.primary }}>
              {t("payments.choose_payment_method")}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            margin: theme.spacing(1, 0),
            [theme.breakpoints.up("md")]: {
              margin: theme.spacing(4, 0),
            },
            display: "flex",
            justifyContent: "center",
            columnGap: theme.spacing(9),
            alignItems: "center",
          }}
        >
          {!isPaymentBankOnly &&
          !externalPaymentUrl &&
          paymentOptionsExist &&
          paymentOptions.find((option) => option.payment_type === "cash")?.active ? (
            <PaymentType>
              <PaymentButton
                aria-label={"payments.pay"}
                active={paymentOption === PaymentOptionEnum.CASH}
                onClick={() => {
                  setPaymentOption(PaymentOptionEnum.CASH);
                }}
              >
                <EuroIcon
                  active={paymentOption === PaymentOptionEnum.CASH}
                  monochrome
                  activeColor={theme.palette.white.main}
                />
              </PaymentButton>
              <Typography
                sx={{
                  color:
                    paymentOption === PaymentOptionEnum.CASH ? theme.palette.primary.main : theme.palette.text.disabled,
                }}
              >
                {t("payments.cash")}
              </Typography>
            </PaymentType>
          ) : null}
          {paymentOptionsExist && paymentOptions.find((option) => option.payment_type === "mip")?.active ? (
            <PaymentType>
              <PaymentButton
                active={paymentOption === PaymentOptionEnum.MIP}
                onClick={() => {
                  setPaymentOption(PaymentOptionEnum.MIP);
                }}
              >
                <BankIcon
                  active={paymentOption === PaymentOptionEnum.MIP}
                  monochrome
                  activeColor={theme.palette.white.main}
                />
              </PaymentButton>
              <Typography
                sx={{
                  color:
                    paymentOption === PaymentOptionEnum.MIP ? theme.palette.primary.main : theme.palette.gullGray.main,
                }}
              >
                {t("payments.bank")}
              </Typography>
            </PaymentType>
          ) : null}
        </Box>
        <ScrollableContainer>
          <Box
            sx={{
              display: "grid",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              gridRowGap: {
                xs: theme.spacing(2),
                lg: theme.spacing(6),
              },
              gridTemplateColumns: "100%",
            }}
          >
            <Box sx={{ display: "grid", gridRowGap: theme.spacing(2) }}>
              {isFetching && <Loader />}
              {total && !isFetching && (
                <Typography component="span" variant="h4" fontWeight="bold">
                  {convertNumberToCurrency(+total)}
                </Typography>
              )}
              <Typography component="span" variant="body2" sx={{ color: theme.palette.text.disabled }}>
                {paymentOption === PaymentOptionEnum.MIP ? t("payments.pay_with_bank") : t("payments.pay_in_cash")}
              </Typography>
            </Box>
            {!isThirdParty && (
              <Box>
                <Button variant="secondary" onClick={handleShowModal} disabled={isFetching}>
                  {t("payments.coupon")}
                </Button>
                {isCouponUsed && (
                  <Typography
                    sx={{
                      color: theme.palette.primary.main,
                    }}
                  >
                    {t("coupons.added_successfully_with_mark")}
                  </Typography>
                )}
              </Box>
            )}
            <PaymentSumsBox discount={discount} sum={sum} commission={commission} isFetchingDiscount={isFetching} />
          </Box>
        </ScrollableContainer>
        <LoadingButton
          sx={{ justifySelf: "center", marginBottom: { xs: "2rem", md: "0" } }}
          variant="contained"
          disableElevation
          loading={isLoading}
          disabled={isLoading}
          onClick={async () => {
            if (orderId) {
              await handlePaymentProcess(orderId);
            }
          }}
        >
          {t("common.continue")}
        </LoadingButton>
        {!isThirdParty && (
          <Modal open={showModal} onClose={handleCloseModal} size="medium" showDefaultConfirmButton={false}>
            <PaymentCoupons
              onGlobalCouponUsed={handleCouponUsed}
              onClientCouponUsed={handleCouponUsed}
              onCouponRemoved={handleCouponRemoved}
              shouldResetError={!showModal}
            />
          </Modal>
        )}
      </StyledPageWrapper>
    </>
  );
};

export default PaymentOverview;
